/**
 * TanStack Query hooks for sales. Keys are namespaced "sales/…".
 *
 * The customer-PII read is deliberately NOT a normal cached query. It is
 * audited per call and answers `no-store`, so it is fetched only when the
 * operator asks, kept out of the persistent cache (`gcTime: 0`), and dropped
 * the moment the screen unmounts. A 410 (snapshot purged) is a final answer,
 * not something to retry.
 */
import { useQuery } from "@tanstack/react-query";

import { useCursorList, type CursorQueryResult, type SortState } from "@/features/shared/ops-api";

import {
  getSalesOrder,
  getSalesOrderCustomerPii,
  listSalesOrders,
  type SalesOrder,
  type SalesOrderCustomerPii,
  type SalesOrderFilters,
  type SalesOrderSummary,
} from "./api";
import {
  getFulfilment,
  listFulfilments,
  type Fulfilment,
  type FulfilmentFilters,
  type FulfilmentSummary,
} from "./fulfilments-api";
import {
  getQuote,
  listQuotes,
  type Quote,
  type QuoteFilters,
  type QuoteSummary,
} from "./quotes-api";

export const salesKeys = {
  fulfilments: {
    all: ["sales/fulfilments"] as const,
    list: (filters: Record<string, unknown>) => ["sales/fulfilments", "list", filters] as const,
    detail: (fulfilmentId: string) => ["sales/fulfilments", "detail", fulfilmentId] as const,
  },
  quotes: {
    all: ["sales/quotes"] as const,
    list: (filters: Record<string, unknown>) => ["sales/quotes", "list", filters] as const,
    detail: (quoteId: string) => ["sales/quotes", "detail", quoteId] as const,
  },
  orders: {
    all: ["sales/orders"] as const,
    list: (filters: Record<string, unknown>) => ["sales/orders", "list", filters] as const,
    detail: (salesOrderId: string) => ["sales/orders", "detail", salesOrderId] as const,
    pii: (salesOrderId: string) => ["sales/orders", "pii", salesOrderId] as const,
  },
};

/** The endpoint publishes no sort parameter — it is created_at,id descending
 *  by contract — so this list takes no SortState. */
export function useSalesOrderList(
  filters: SalesOrderFilters,
  limit?: number,
): CursorQueryResult<SalesOrderSummary> {
  const sort: SortState = null;
  return useCursorList<SalesOrderSummary>(
    salesKeys.orders.list(filters),
    (args) => listSalesOrders(filters, args),
    limit === undefined ? { sort } : { sort, limit },
  );
}

export function useSalesOrder(salesOrderId: string | undefined) {
  return useQuery<SalesOrder>({
    queryKey: salesKeys.orders.detail(salesOrderId ?? ""),
    queryFn: ({ signal }) => getSalesOrder(salesOrderId ?? "", signal),
    enabled: salesOrderId !== undefined && salesOrderId !== "",
  });
}

/** Enabled only while the operator is actively revealing; every enable is one
 *  audited access. */
export function useSalesOrderPii(salesOrderId: string, enabled: boolean) {
  return useQuery<SalesOrderCustomerPii>({
    queryKey: salesKeys.orders.pii(salesOrderId),
    queryFn: ({ signal }) => getSalesOrderCustomerPii(salesOrderId, signal),
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Quotes are ordered created_at,id descending by contract, so no SortState. */
export function useQuoteList(
  filters: QuoteFilters,
  limit?: number,
): CursorQueryResult<QuoteSummary> {
  const sort: SortState = null;
  return useCursorList<QuoteSummary>(
    salesKeys.quotes.list(filters),
    (args) => listQuotes(filters, args),
    limit === undefined ? { sort } : { sort, limit },
  );
}

export function useQuote(quoteId: string | undefined) {
  return useQuery<Quote>({
    queryKey: salesKeys.quotes.detail(quoteId ?? ""),
    queryFn: ({ signal }) => getQuote(quoteId ?? "", signal),
    enabled: quoteId !== undefined && quoteId !== "",
  });
}

/** Fulfilments are ordered created_at,id descending by contract. */
export function useFulfilmentList(
  filters: FulfilmentFilters,
  limit?: number,
): CursorQueryResult<FulfilmentSummary> {
  const sort: SortState = null;
  return useCursorList<FulfilmentSummary>(
    salesKeys.fulfilments.list(filters),
    (args) => listFulfilments(filters, args),
    limit === undefined ? { sort } : { sort, limit },
  );
}

export function useFulfilment(fulfilmentId: string | undefined) {
  return useQuery<Fulfilment>({
    queryKey: salesKeys.fulfilments.detail(fulfilmentId ?? ""),
    queryFn: ({ signal }) => getFulfilment(fulfilmentId ?? "", signal),
    enabled: fulfilmentId !== undefined && fulfilmentId !== "",
  });
}
